import { FC, useState } from 'react';
import { Alert, Button, Checkbox, Form, Input } from 'antd';
import { useLocation, useNavigate } from 'react-router';
import { useIntl } from 'react-intl';
import { Code, ConnectError } from '@bufbuild/connect';

import { AuthState_AuthStatus } from 'protocol/api/auth/dto_auth_pb';
import { useAppDispatch } from '@/shared/hooks';
import { authModel } from '@/entities/auth';
import { MFARequirementCase, PATHS } from '@/shared/config';
import { normalizeEmail, safeJSONParse } from '@/shared/lib';

import { useSignInRetryCountdown } from './hooks';
import { MFAChallenge } from './ui';

import styles from './SignIn.module.scss';

export const SignIn: FC = () => {
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState<string>();
  const [isCountdownAfterFailure, setIsCountdownAfterFailure] = useState(false);
  const [mfaRequirement, setMFARequirement] =
    useState<MFARequirementCase>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { formatMessage } = useIntl();

  const { secondsLeft, startCountdown } = useSignInRetryCountdown();
  const isRetryCountdownActive = secondsLeft > 0;

  const from = (location?.state as { from: Location })?.from;

  const onSubmit = async (data: { email: string; password: string }) => {
    const { email, password } = data;

    setLoading(true);

    try {
      const { Status, MFA } = await dispatch(
        authModel.thunks.signIn({
          Email: normalizeEmail(email),
          Password: password,
        }),
      ).unwrap();

      if (Status === AuthState_AuthStatus.SUCCESSFUL) {
        navigate(from || PATHS.main);
        return;
      }

      setMFARequirement(MFA?.Requirement?.case ?? null);
    } catch (error) {
      const { code, rawMessage } = error as ConnectError;
      const parsedMessage = safeJSONParse(rawMessage);
      const userBadRequestCode =
        code === Code.FailedPrecondition && parsedMessage.code;
      const { retryAfterMs } = parsedMessage;

      if (userBadRequestCode === 'login_invalid_creds') {
        setGlobalError(
          formatMessage({
            id: 'signIn.invalidCredsError',
            defaultMessage: 'Incorrect email or password.',
          }),
        );

        if (typeof retryAfterMs === 'number' && retryAfterMs > 0) {
          setIsCountdownAfterFailure(true);
          startCountdown(retryAfterMs);
        }
      } else if (userBadRequestCode === 'login_throttled') {
        if (typeof retryAfterMs === 'number' && retryAfterMs > 0) {
          setGlobalError(undefined);
          setIsCountdownAfterFailure(false);
          startCountdown(retryAfterMs);
        } else {
          setGlobalError(
            formatMessage({
              id: 'signIn.waitBeforeRetryError',
              defaultMessage:
                'Please wait a few seconds before signing in again.',
            }),
          );
        }
      } else if (userBadRequestCode === 'password_reset_required') {
        setGlobalError(
          formatMessage({
            id: 'signIn.passwordResetRequiredError',
            defaultMessage:
              'Your password is too short to use without two-step verification. Reset it in the Diagnocat web app to sign in.',
          }),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const retryCountdownError = isCountdownAfterFailure
    ? formatMessage(
        {
          id: 'signIn.invalidCredsRetryCountdownError',
          defaultMessage:
            'Incorrect email or password. Try again in {seconds, plural, one {# second} other {# seconds}}.',
        },
        { seconds: secondsLeft },
      )
    : formatMessage(
        {
          id: 'signIn.retryCountdownError',
          defaultMessage:
            'Too many attempts. Try again in {seconds, plural, one {# second} other {# seconds}}.',
        },
        { seconds: secondsLeft },
      );

  const formError = isRetryCountdownActive ? retryCountdownError : globalError;

  if (mfaRequirement) {
    return (
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <MFAChallenge
            requirement={mfaRequirement}
            onCompleted={() => navigate(from || PATHS.main)}
            onCancel={() => setMFARequirement(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <Form
          name="sign-in"
          layout="vertical"
          style={{ maxWidth: 600 }}
          initialValues={{ remember: true }}
          onChange={() => setGlobalError(undefined)}
          onFinish={onSubmit}
          disabled={loading}
        >
          <Form.Item
            label={formatMessage({
              id: 'signIn.label.email',
              defaultMessage: 'Email',
            })}
            name="email"
            rules={[
              {
                required: true,
                message: formatMessage({
                  id: 'signIn.validation.email',
                  defaultMessage: 'Please input your Email!',
                }),
              },
            ]}
          >
            <Input type="email" autoComplete="email" inputMode="email" />
          </Form.Item>

          <Form.Item
            label={formatMessage({
              id: 'signIn.label.password',
              defaultMessage: 'Password',
            })}
            name="password"
            rules={[
              {
                required: true,
                message: formatMessage({
                  id: 'signIn.validation.password',
                  defaultMessage: 'Please input your password!',
                }),
              },
            ]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>

          <Form.Item name="remember" valuePropName="checked">
            <Checkbox>
              {formatMessage({
                id: 'signIn.label.remember',
                defaultMessage: 'Remember me',
              })}
            </Checkbox>
          </Form.Item>

          {formError ? (
            <Form.Item>
              <Alert type="error" message={formError} showIcon />
            </Form.Item>
          ) : null}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              disabled={loading || isRetryCountdownActive}
            >
              {formatMessage({
                id: 'signIn.submitButton',
                defaultMessage: 'Submit',
              })}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};
